import { computed, Injectable, signal } from '@angular/core';

interface SaveState {
  pending: number;
  failed: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class StatusService {
  private changes = new Set<number>();
  private saves = new Map<string, SaveState>();
  private nextChangeId = 0;
  private currentStatus = signal(SmallStatusEnum.nothing);

  readonly status = this.currentStatus.asReadonly();
  readonly pending = computed(() => this.status() === SmallStatusEnum.loading);

  beginChange(): number {
    const changeId = ++this.nextChangeId;
    this.changes.add(changeId);
    this.updateStatus();
    return changeId;
  }

  finishChange(changeId: number | null): void {
    if (changeId === null) {
      return;
    }

    this.changes.delete(changeId);
    this.updateStatus();
  }

  startSave(key: string): void {
    let save = this.saves.get(key);
    if (!save) {
      save = { pending: 0, failed: false };
      this.saves.set(key, save);
    }

    save.pending += 1;
    save.failed = false;
    this.updateStatus();
  }

  // Field effects discard the previous response when a newer edit is submitted.
  replaceSave(key: string): void {
    this.saves.set(key, { pending: 1, failed: false });
    this.updateStatus();
  }

  finishSave(key: string, failed = false): void {
    const save = this.saves.get(key);
    if (!save || save.pending === 0) {
      return;
    }

    save.pending -= 1;
    save.failed = failed;
    this.updateStatus();
  }

  reset(): void {
    // Do not reset nextChangeId: cleanup from the previous record may still arrive.
    this.changes.clear();
    this.saves.clear();
    this.currentStatus.set(SmallStatusEnum.nothing);
  }

  private updateStatus(): void {
    if (this.changes.size > 0) {
      this.currentStatus.set(SmallStatusEnum.loading);
      return;
    }

    let hasFailedSave = false;
    for (const save of this.saves.values()) {
      if (save.pending > 0) {
        this.currentStatus.set(SmallStatusEnum.loading);
        return;
      }
      if (save.failed) {
        hasFailedSave = true;
      }
    }

    if (hasFailedSave) {
      this.currentStatus.set(SmallStatusEnum.error);
    } else if (this.saves.size > 0) {
      this.currentStatus.set(SmallStatusEnum.done);
    } else {
      this.currentStatus.set(SmallStatusEnum.nothing);
    }
  }
}

export enum SmallStatusEnum {
  nothing,
  loading,
  done,
  error,
}
